(() => {
  if (window.campaignNav) {
    window.campaignNav.init(document);
    return;
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const escapeAttrValue = (value) => {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(value);
    }
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  };

  const findTargetEl = (key) => {
    if (!key) return null;
    return (
      document.querySelector(`[data-nav-target="${escapeAttrValue(key)}"]`) ||
      document.getElementById(key)
    );
  };

  const getVisibleLabel = (link) => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const selector = isMobile ? ".campaign-nav__label.hide-pc" : ".campaign-nav__label.hide-mb";
    const el = link.querySelector(selector) || link.querySelector(".campaign-nav__label");
    return (el?.textContent || link.textContent || "").replace(/\s+/g, " ").trim();
  };

  const readIndicatorInset = (nav) => {
    const value = Number.parseFloat(nav.getAttribute("data-campaign-nav-inset"));
    if (!Number.isFinite(value) || value < 0 || value >= 0.5) return 0.18;
    return value;
  };

  const getSections = (links) =>
    links
      .map((link) => {
        const id = link.dataset.campaignNavTarget;
        if (!id) return null;
        const el = findTargetEl(id);
        return el ? { link, el, id } : null;
      })
      .filter(Boolean);

  const setup = (nav) => {
    if (nav.dataset.campaignNavInitialized === "true") return;

    const track = nav.querySelector("[data-campaign-nav-track]");
    const indicator = nav.querySelector("[data-campaign-nav-indicator]");
    const links = [...nav.querySelectorAll("[data-campaign-nav-link]")];
    const sections = getSections(links);

    if (!track || !indicator || !sections.length) return;

    const inset = readIndicatorInset(nav);
    const indicatorScale = 1 - inset * 2;
    const indicatorDebounceMs = 100;
    let activeIndex = 0;
    let ticking = false;
    let indicatorTimer = 0;

    const scrollActiveItemIntoView = (item) => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (!item || maxScroll <= 1) return;

      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemLeft = itemRect.left - trackRect.left + track.scrollLeft;
      const itemRight = itemLeft + itemRect.width;
      const viewLeft = track.scrollLeft;
      const viewRight = viewLeft + track.clientWidth;

      let next = viewLeft;
      if (itemRect.width >= track.clientWidth || itemLeft < viewLeft) {
        next = itemLeft;
      } else if (itemRight > viewRight) {
        next = itemRight - track.clientWidth;
      }

      next = Math.max(0, Math.min(maxScroll, next));
      if (Math.abs(next - viewLeft) < 1) return;

      track.scrollLeft = next;
    };

    const applyIndicator = () => {
      const item = sections[activeIndex]?.link.closest(".campaign-nav__item") || sections[activeIndex]?.link;
      if (!item) return;

      scrollActiveItemIntoView(item);

      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const width = itemRect.width;
      const left = itemRect.left - trackRect.left + track.scrollLeft + width * inset;
      const nextWidth = `${Math.round(width * indicatorScale)}px`;
      const nextTransform = `translateX(${Math.round(left)}px)`;

      if (indicator.style.width === nextWidth && indicator.style.transform === nextTransform) return;

      indicator.style.width = nextWidth;
      indicator.style.transform = nextTransform;
    };

    const moveIndicator = (immediate = false) => {
      window.clearTimeout(indicatorTimer);
      indicatorTimer = 0;
      if (immediate) {
        applyIndicator();
        return;
      }
      indicatorTimer = window.setTimeout(applyIndicator, indicatorDebounceMs);
    };

    const setActive = (index, immediate = false) => {
      if (index < 0 || index >= sections.length) return;
      activeIndex = index;

      sections.forEach((section, i) => {
        section.link.classList.toggle("is-active", i === index);
        section.link.setAttribute("aria-current", i === index ? "true" : "false");
      });

      moveIndicator(immediate);
    };

    /**
     * Active once this section's top has reached ~viewport mid,
     * until the next nav target's top reaches that same line.
     */
    const updateActiveFromScroll = () => {
      const threshold = window.innerHeight * 0.5;
      let nextActive = 0;

      sections.forEach((section, index) => {
        if (section.el.getBoundingClientRect().top <= threshold) {
          nextActive = index;
        }
      });

      if (nextActive !== activeIndex) {
        setActive(nextActive);
      } else {
        moveIndicator();
      }
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateActiveFromScroll();
        ticking = false;
      });
    };

    const pushNavGa4 = (buttonName) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push({
        event: "ga4Event",
        event_name: "select_content_category",
        event_parameters: {
          content_type: "navigation_bar",
          button_name: buttonName,
        },
      });
    };

    const scrollToSection = (index) => {
      const section = sections[index];
      if (!section) return;

      section.el.scrollIntoView({
        behavior: reduceMotion.matches ? "auto" : "smooth",
        block: "start",
      });

      setActive(index, true);
    };

    links.forEach((link) => {
      link.addEventListener("click", (event) => {
        const targetIndex = sections.findIndex((section) => section.link === link);
        if (targetIndex < 0) return;
        event.preventDefault();
        pushNavGa4(getVisibleLabel(link));
        scrollToSection(targetIndex);
      });
    });

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    setActive(0);
    updateActiveFromScroll();
    moveIndicator(true);

    nav.dataset.campaignNavInitialized = "true";
  };

  const init = (root = document) => {
    if (root.matches?.("[data-campaign-nav]")) {
      setup(root);
    }
    root.querySelectorAll?.("[data-campaign-nav]").forEach(setup);
  };

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  window.campaignNav = { init };
  init();
})();
